import { Stack } from '@mui/material';
import { RadioGroup } from 'common/form';
import { Header } from 'common/ui/Header';
import { useGetEditCampaignQuery } from 'generated-graphql/hooks/apollo-hooks';
import {
  CampaignCreationCSVColumns,
  CampaignCreationCSVUpload
} from 'modules/campaign-creation/components/CampaignCreationCSVUpload';
import {
  OverlappingCampaignAction,
  OVERLAPPING_CAMPAIGNS_LABEL,
  overlappingCampaignsRadioOptions
} from 'modules/campaigns/components/SwitchCampaignOptionLabel';
import { useNavigate, useParams } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { Loading } from 'common/ui/Loading';
import {
  createCampaignCsvKeys,
  startGeneratingMessages
} from 'services/campaign/api';
import { Flags } from 'services/flags/constants';
import { useFlag } from 'services/flags/hooks';
import { useState } from 'react';

export const CampaignAddMoreCSVCandidates = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [closePreviousConversations, setClosePreviousConversations] =
    useState(false);
  const isCandidatePrioritisationEnabled = useFlag(
    Flags.CAMPAIGN_CANDIDATE_PRIORITISATION
  );
  const { data: campaign, loading: isLoadingCampaign } =
    useGetEditCampaignQuery({
      variables: {
        id: campaignId || ''
      },
      skip: !campaignId
    });

  const handleCSVImportCandidates = async (values: {
    csvS3Key: string;
    csvColumns: CampaignCreationCSVColumns;
  }) => {
    const organizationId = campaign?.getCampaign?.organizationId;

    if (!campaignId || !organizationId) {
      return;
    }

    setIsLoading(true);

    await createCampaignCsvKeys({
      csvS3Key: values.csvS3Key,
      campaignId: campaignId,
      organizationId
    });
    await startGeneratingMessages({ campaignId, closePreviousConversations });

    setIsLoading(false);
    // Pass state to indicate this is an "add candidates" flow, not a new campaign
    navigate(`${Paths.CAMPAIGN_OUTBOUND_OPENING_MESSAGES}/${campaignId}`, {
      state: { isAddingCandidates: true, closePreviousConversations }
    });
  };

  const campaignData = campaign?.getCampaign;

  if (isLoadingCampaign || !campaignData || isLoading) {
    return <Loading />;
  }

  return (
    <Stack spacing={3}>
      <Header
        currentBreadcrumb="Add candidates"
        title="Add candidates"
        subtitle={campaignData?.title}
        breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
        onBack={() => navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`)}
      />

      {isCandidatePrioritisationEnabled && (
        <RadioGroup
          name="closePreviousConversations"
          value={
            closePreviousConversations
              ? OverlappingCampaignAction.SWITCH
              : OverlappingCampaignAction.WAIT
          }
          field={{
            containerSx: { maxWidth: 400 },
            label: OVERLAPPING_CAMPAIGNS_LABEL,
            options: overlappingCampaignsRadioOptions
          }}
          onChange={(value) => {
            setClosePreviousConversations(
              value === OverlappingCampaignAction.SWITCH
            );
          }}
        />
      )}

      <CampaignCreationCSVUpload
        channel={campaignData?.channel || null}
        emailFallbackEnabled={campaignData?.emailChannelFallbackEnabled ?? null}
        csvImportState={null}
        onCSVImportCandidates={handleCSVImportCandidates}
        onCSVImportSkip={null}
      />
    </Stack>
  );
};
